import {
  getCalendarIntegrationType,
  getHasUserGivenCalendarConsent,
  getIsGoogleIntegrationActive,
  getIsMeetingsCalendarEnabled,
  getIsUserCalendarConsentInvalid,
  getMeetingsByDate,
  getMeetingsLoadingStatusByDate,
  redirectForCalendarConsent,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {useAppDispatch, useAppSelector, useFeatureToggle, useModal} from '@hooks';

import {CalendarEventsListProps} from './types';
import {parseISO, startOfToday} from 'date-fns';

import {useTranslation} from 'react-i18next';
import {Illustration} from '@atoms';
import {Button, SquareButton} from '@molecules';
import {CalendarEventCell, CreateEventCard} from '@organisms';
import {isBeforeToday} from '@lib/utils';
import {CardWrapper, CardWrapperEmpty} from './styles';

export const CalendarEventsList = ({date}: CalendarEventsListProps) => {
  const dateAsDate = parseISO(date);
  const {addPages, openModal} = useModal();
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const meetings = useAppSelector((state) => getMeetingsByDate(state, dateAsDate));
  const isCalendarConsentGiven = useAppSelector(getHasUserGivenCalendarConsent);
  const isCalendarConsentInvalid = useAppSelector(getIsUserCalendarConsentInvalid);
  const isFailed = useAppSelector((state) => getMeetingsLoadingStatusByDate(state, dateAsDate)) === 'Failed';
  const isCalendarEnabledByAdmin = useAppSelector(getIsMeetingsCalendarEnabled);
  const isGoogleIntegrationActive = useAppSelector(getIsGoogleIntegrationActive);
  const calendarIntegrationType = useAppSelector(getCalendarIntegrationType);
  const isPast = isBeforeToday(dateAsDate, startOfToday());

  const {HybridMeetingsCalendarView, MeetingCreation, MeetingBookingGoogle} = useFeatureToggle();

  const shouldShowMeetingCreationButton = isPast // Remove the button for the days in the past
    ? false
    : isGoogleIntegrationActive && !MeetingBookingGoogle // Meeting creation using Google feature is not enabled
    ? false
    : HybridMeetingsCalendarView &&
      MeetingCreation &&
      isCalendarEnabledByAdmin &&
      isCalendarConsentGiven &&
      !isCalendarConsentInvalid;

  const handleOpenCreateEvent = () => {
    addPages([<CreateEventCard date={date} />]);
    openModal();
  };

  const title = isCalendarConsentInvalid
    ? t('meeting:LostCalendarConnectionBannerTitle')
    : !isCalendarConsentGiven
    ? t('meeting:CalendarBannerTitle')
    : null;

  const message = isCalendarConsentInvalid
    ? t('meeting:LostCalendarConnectionBannerMessage')
    : !isCalendarConsentGiven
    ? t('meeting:CalendarBannerMessage')
    : isFailed
    ? t('meeting:ErrorOnLoadingMeetings')
    : t('meeting:NoMeetingsForDay');

  const buttonLabel = isCalendarConsentInvalid
    ? t('meeting:LostCalendarConnectionBannerButton')
    : !isCalendarConsentGiven && calendarIntegrationType
    ? t('meeting:CalendarBannerButton', {calendarIntegrationType})
    : null;

  const illustration = isCalendarConsentInvalid
    ? 'error'
    : !isCalendarConsentGiven
    ? 'calendar'
    : isFailed
    ? 'error'
    : 'calendar';

  const ariaLabel = isCalendarConsentInvalid
    ? 'Calendar connection is lost'
    : !isCalendarConsentGiven
    ? 'Calendar consent is not given'
    : isFailed
    ? 'Something went wrong'
    : 'No meetings for today';

  const handleButtonClick = () => {
    dispatch(withAsyncThunkErrorHandling(() => redirectForCalendarConsent()));
  };

  return (
    <CardWrapper>
      {shouldShowMeetingCreationButton && (
        <SquareButton
          iconLeft="calendarPlus"
          iconRight="caretRight"
          data-testid="organisms-calendar-CalendarEventsList_create-event-button"
          squareButton="transparent"
          onClick={handleOpenCreateEvent}>
          {t('meeting:CreateMeetingCreateEventButton')}
        </SquareButton>
      )}
      {isCalendarConsentInvalid || !isCalendarConsentGiven || meetings.length === 0 || isFailed ? (
        <CardWrapperEmpty>
          <Illustration
            illustration={illustration}
            size="100px"
            aria-label={ariaLabel}
          />
          <div>
            {title && <b>{title}</b>}
            <p>{message}</p>
          </div>
          {!!buttonLabel && (
            <Button
              data-testid="organisms-calendar-CalendarEventsList_connect-calendar-button-fs"
              button="tertiary"
              onClick={handleButtonClick}
              size="small">
              {buttonLabel}
            </Button>
          )}
        </CardWrapperEmpty>
      ) : (
        meetings.map((meeting) => (
          <CalendarEventCell
            meeting={meeting}
            date={date}
            key={meeting.id}
          />
        ))
      )}
    </CardWrapper>
  );
};
